import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UIIconComponent, UiInputComponent } from '@design-system';
import { OrderSide } from '@shared-types';

import { KeyboardDirective } from '../../../../components/keyboard';
import { OrderFormFacade } from '../../data-access/order-form.facade';
import {
	NO_BUYING_POWER_TOAST,
	NO_SELLABLE_ASSET_TOAST,
	ORDER_CALCULATOR_LABEL,
	ORDER_MAX_LABEL,
	ORDER_MIN_LABEL
} from '../../constants/order-calculator.constants';
import { ToastService } from '../../../../services/toast.service';

import {
	calculateOrderValueFromQuantity,
	calculateQuantityFromAssetPercent,
	calculateQuantityFromOrderValue,
	calculateValueFromBuyingPowerPercent,
	formatWithSeparator,
	parseFormattedNumber,
	percentFromAssets,
	percentFromBuyingPower
} from '../../tools/order-calculator.tools';

/**
 * Order amount calculator (مبلغ سفارش).
 *
 * Buy:
 *   amount (typed | % × buyingPower) — always filled, even without price
 *   quantity = floor(amount / (price × (1 + buyCommission))) — only when price exists
 *   slider % = amount / buyingPower
 *   no buying power → slider / max locked + toast
 *
 * Quantity (typed in the quantity field, either side):
 *   amount = quantity × price ± commission — recomputed as soon as a price exists
 *
 * Sell:
 *   typed amount → quantity (needs price)
 *   slider % → quantity = round(% × assets) → amount (needs price)
 *   no price → slider / max do not change quantity or amount
 *   no assets → slider / max locked + toast
 */
@Component({
	selector: 'app-order-calculator',
	templateUrl: './order-calculator.component.html',
	styleUrl: './order-calculator.component.scss',
	imports: [FormsModule, UiInputComponent, KeyboardDirective, UIIconComponent]
})
export class OrderCalculatorComponent {
	private readonly toastService = inject(ToastService);
	private readonly orderFormFacade = inject(OrderFormFacade);

	readonly labels = {
		title: ORDER_CALCULATOR_LABEL,
		max: ORDER_MAX_LABEL,
		min: ORDER_MIN_LABEL
	} as const;

	readonly sliderTicks = [0, 25, 50, 75, 100] as const;

	/** Order amount (مبلغ) — held on the facade so it survives the panel and the price field sees it. */
	private readonly amount = this.orderFormFacade.orderValue;

	/** Raw percent while dragging so the thumb does not jump from rounding. */
	private readonly dragPercent = signal<number | null>(null);

	/**
	 * Last user intent — used when price/commission changes:
	 * - amount: re-derive quantity from amount
	 * - percent: re-derive amount from quantity (sell after slider / max)
	 * - quantity: user typed the quantity field → re-derive amount from quantity
	 */
	private readonly lastSource = signal<'amount' | 'percent' | 'quantity'>('amount');

	/** Last quantity written or observed — tells our own writes from quantity-field edits. */
	private lastQuantity: number | null = null;

	readonly isSliderDragging = signal(false);
	readonly isSliderHovered = signal(false);

	readonly side = computed(() => this.orderFormFacade.orderFormModel().side);
	readonly isBuy = computed(() => this.side() === OrderSide.Buy);

	readonly price = computed(() => this.orderFormFacade.orderFormModel().price ?? 0);
	readonly commission = computed(() =>
		this.isBuy() ? this.orderFormFacade.buyCommissionRate() : this.orderFormFacade.sellCommissionRate()
	);
	readonly buyingPower = computed(() => this.orderFormFacade.buyingPower());
	readonly assetQuantity = computed(() => this.orderFormFacade.currentAsset());
	readonly quantity = computed(() => this.orderFormFacade.orderFormModel().quantity ?? 0);

	/** Current amount after user edits. */
	readonly currentAmount = this.amount.asReadonly();

	/**
	 * No headroom on the active side: the percent feature is not draggable, because every
	 * percentage of zero is zero and applying it would wipe an already-entered quantity.
	 */
	readonly sliderLocked = computed(() => (this.isBuy() ? this.buyingPower() <= 0 : this.assetQuantity() <= 0));

	/** Locked-slider toast text — names the side's missing headroom. */
	private readonly lockedToast = computed(() => (this.isBuy() ? NO_BUYING_POWER_TOAST : NO_SELLABLE_ASSET_TOAST));

	readonly sliderTooltipVisible = computed(
		() => !this.sliderLocked() && (this.isSliderDragging() || this.isSliderHovered())
	);

	readonly formattedValue = computed(() => {
		const amount = this.amount();
		return amount > 0 ? formatWithSeparator(amount) : '';
	});

	/**
	 * Progress position:
	 * - Buy: % of buying power from amount
	 * - Sell: % of assets from quantity
	 */
	readonly sliderPercent = computed(() => {
		if (this.sliderLocked()) {
			return 0;
		}
		const drag = this.dragPercent();
		if (drag !== null) {
			return drag;
		}
		if (this.isBuy()) {
			return percentFromBuyingPower(this.amount(), this.buyingPower());
		}
		return percentFromAssets(this.quantity(), this.assetQuantity());
	});

	constructor() {
		// Keep quantity/amount in sync when the quantity field, price, or commission changes.
		effect(() => {
			const quantity = this.quantity();
			const price = this.price();
			const commission = this.commission();
			const side = this.side();

			untracked(() => this.syncWithForm(side, quantity, price, commission));
		});
	}

	/** User typed (or cleared) the amount input. */
	onValueInput(raw: string | number): void {
		this.applyAmount(parseFormattedNumber(raw));
	}

	onMinClick(): void {
		const price = this.price();
		if (!price) {
			this.orderFormFacade.calculatorPriceRequired.set(true);
			return;
		}

		this.orderFormFacade.calculatorPriceRequired.set(false);
		this.lastSource.set('quantity');
		this.writeQuantity(1);
		this.commitAmount(calculateOrderValueFromQuantity(this.side(), 1, price, this.commission()));
	}

	/**
	 * Max on amount input:
	 * - Buy → full buying power as amount (price not required)
	 * - Sell → full asset quantity → amount (if price exists)
	 * - No headroom on the active side → toast (same as locked slider)
	 */
	onMaxClick(): void {
		if (this.sliderLocked()) {
			this.notifySliderLocked();
			return;
		}
		if (this.isBuy()) {
			this.applyAmount(Math.round(this.buyingPower()));
			return;
		}
		this.applySellPercentQuantity(this.assetQuantity());
	}

	onSliderInput(event: Event): void {
		if (this.sliderLocked()) {
			this.notifySliderLocked();
			return;
		}
		this.onSliderChange(Number((event.target as HTMLInputElement).value));
	}

	/**
	 * Percent feature (0–100):
	 * - Buy → amount = % × buyingPower (always) → quantity only if price exists
	 * - Sell → quantity = round(% × assets) → amount (needs price; no-op without price)
	 */
	onSliderChange(percent: number): void {
		if (this.sliderLocked()) {
			return;
		}

		// Sell percent needs price — do not move quantity/amount without it.
		if (!this.isBuy() && !this.price()) {
			return;
		}

		const clamped = Math.min(100, Math.max(0, percent));
		this.dragPercent.set(clamped);
		this.lastSource.set('percent');

		if (this.isBuy()) {
			// Amount depends only on buying power — price is not required.
			this.commitAmount(calculateValueFromBuyingPowerPercent(this.buyingPower(), clamped));
			this.deriveQuantityFromAmount();
			return;
		}

		this.applySellPercentQuantity(calculateQuantityFromAssetPercent(this.assetQuantity(), clamped));
	}

	onSliderPointerDown(): void {
		if (!this.sliderLocked()) {
			this.isSliderDragging.set(true);
		}
	}

	onSliderPointerUp(): void {
		this.isSliderDragging.set(false);
		this.dragPercent.set(null);
	}

	onSliderHover(hovered: boolean): void {
		this.isSliderHovered.set(hovered);
	}

	/** Soft-lock: toast when user tries to drag the percent with no headroom on the active side. */
	onLockedSliderInteraction(event: Event): void {
		event.preventDefault();
		event.stopPropagation();
		this.notifySliderLocked();
	}

	private notifySliderLocked(): void {
		this.toastService.warning(this.lockedToast());
	}

	/** Amount-driven path (typing / min / buy-max). */
	private applyAmount(amount: number): void {
		this.lastSource.set('amount');
		this.commitAmount(amount);
		this.deriveQuantityFromAmount();
	}

	/**
	 * Sell percent / sell-max path: quantity first, then amount from quantity.
	 * No price → no-op (quantity and amount stay unchanged).
	 */
	private applySellPercentQuantity(rawQuantity: number): void {
		const price = this.price();
		if (!price) {
			return;
		}

		this.lastSource.set('percent');
		const quantity = Math.max(0, rawQuantity);
		this.writeQuantity(quantity);
		this.commitAmount(calculateOrderValueFromQuantity(OrderSide.Sell, quantity, price, this.commission()));
	}

	/** Writes the order amount. Independent of price. */
	private commitAmount(amount: number): void {
		this.amount.set(Math.max(0, amount));
	}

	/**
	 * Quantity from amount + price + commission.
	 * No-op when price is missing — amount stays as entered / from buying power.
	 * Clears quantity when amount is cleared and price exists.
	 */
	private deriveQuantityFromAmount(): void {
		const price = this.price();
		if (!price) {
			return;
		}

		const amount = this.amount();
		if (amount <= 0) {
			this.writeQuantity(0);
			return;
		}

		this.writeQuantity(calculateQuantityFromOrderValue(this.side(), amount, price, this.commission()));
	}

	/**
	 * Reacts to quantity-field edits and to price/commission changes.
	 * A quantity typed outside the calculator becomes the driver: the amount follows it.
	 */
	private syncWithForm(side: OrderSide, quantity: number, price: number, commission: number): void {
		const isFirstRun = this.lastQuantity === null;
		const typedInQuantityField = !isFirstRun && quantity !== this.lastQuantity;
		this.lastQuantity = quantity;

		// Opened on top of an already entered quantity with no amount of its own.
		if (typedInQuantityField || (isFirstRun && quantity > 0 && this.amount() <= 0)) {
			this.lastSource.set('quantity');
		}

		if (!price) {
			return;
		}

		if (this.lastSource() === 'quantity') {
			this.commitAmount(quantity > 0 ? calculateOrderValueFromQuantity(side, quantity, price, commission) : 0);
			return;
		}

		if (side === OrderSide.Buy || this.lastSource() === 'amount') {
			const amount = this.amount();
			if (amount <= 0) {
				return;
			}
			this.writeQuantity(calculateQuantityFromOrderValue(side, amount, price, commission));
			return;
		}

		// Sell after percent/max: keep quantity, refresh amount.
		if (quantity <= 0) {
			return;
		}
		this.commitAmount(calculateOrderValueFromQuantity(OrderSide.Sell, quantity, price, commission));
	}

	private writeQuantity(qty: number): void {
		const next = Math.max(0, qty);
		this.lastQuantity = next;
		if (this.orderFormFacade.orderFormModel().quantity === next) {
			return;
		}
		this.orderFormFacade.setFormData({ quantity: next });
	}
}
